/**
 * Access model — 4-layer role gate for the Sprint 26 auth work.
 *
 * Tickets:
 *  - 86e232yn7  Create 4-layer security group + role structure
 *  - 86e22kunv  Admin vs non-admin role-gated UI (authorization baseline)
 *  - 86e22kuvm  Unauthenticated/public view restrictions + pilot write access
 *
 * Public (unauthenticated) surface: Drones + OI + Surveillance. Surveillance was
 * unlocked read-only for the ArduPilot conference (ENG-279); its write actions
 * stay behind `surveillance:write` / `service:fuse`. Every other context
 * prompts a login.
 *
 * Roles come from Keycloak claims (see mapOidcUser in keycloak.ts). The demo
 * role picker is commented out of LoginModal (ENG-278).
 */
import type { Context } from '@/lib/store/ui';

/** `public` = unauthenticated visitor. Authenticated tiers: user < pilot < admin. */
export type Role = 'public' | 'user' | 'pilot' | 'admin';

/** Authenticated roles selectable in the mock login flow. */
export const LOGIN_ROLES = ['user', 'pilot', 'admin'] as const;
export type LoginRole = (typeof LOGIN_ROLES)[number];

export const ROLE_LABEL: Record<LoginRole, string> = {
  user: 'User',
  pilot: 'Pilot',
  admin: 'Administrator',
};

/** Human-friendly context names (used by the login gate + lock affordances). */
export const CONTEXT_LABEL: Record<Context, string> = {
  drones: 'Drones',
  oi: 'Operational Intents',
  flights: 'Flights',
  surv: 'Surveillance',
  infra: 'Infrastructure',
  rtm: 'RTM Services',
  inv: 'Inventory',
  ds: 'Data Stream',
  apps: 'Apps',
};

/** Contexts visible without authenticating (team decision — see file header). */
export const PUBLIC_CONTEXTS = ['drones', 'oi', 'surv'] as const satisfies readonly Context[];

export function canViewContext(ctx: Context, role: Role): boolean {
  // Any authenticated role gets at least a read-only view of every context.
  if (role !== 'public') return true;
  return (PUBLIC_CONTEXTS as readonly Context[]).includes(ctx);
}

/**
 * Write / management capabilities layered above the read-only baseline.
 *  - user  : read-only (no capabilities)
 *  - pilot : + write access to Inventory + Surveillance
 *  - admin : + tenant partnership management + service-fusing
 */
export type Capability =
  | 'inventory:write'
  | 'surveillance:write'
  | 'tenant:manage'
  | 'service:fuse';

const CAPABILITIES: Record<Role, readonly Capability[]> = {
  public: [],
  user: [],
  pilot: ['inventory:write', 'surveillance:write'],
  admin: ['inventory:write', 'surveillance:write', 'tenant:manage', 'service:fuse'],
};

export function can(capability: Capability, role: Role): boolean {
  return CAPABILITIES[role].includes(capability);
}
